import { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Search, AlertTriangle, CheckCircle2, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function AdminArticlesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [filterCat, setFilterCat] = useState('');
    const [edits, setEdits] = useState({});
    const [savingNom, setSavingNom] = useState(null);

    const fetchArticles = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/articles/base`, { withCredentials: true });
            setArticles(data || []);
        } catch (e) {
            toast.error('Erreur de chargement');
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchArticles(); }, []);

    const categories = useMemo(() => {
        const set = new Set(articles.map(a => a.categorie || '—'));
        return ['', ...Array.from(set).sort()];
    }, [articles]);

    const filtered = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return articles.filter(a => {
            if (filterCat && (a.categorie || '—') !== filterCat) return false;
            if (q && !a.nom.toLowerCase().includes(q) && !(a.categorie || '').toLowerCase().includes(q)) return false;
            return true;
        });
    }, [articles, searchText, filterCat]);

    const counts = useMemo(() => {
        const total = articles.length;
        const renseignes = articles.filter(a => a.volume && a.volume > 0).length;
        return { total, renseignes, manquants: total - renseignes };
    }, [articles]);

    if (user?.role !== 'admin') return <Navigate to="/" replace />;

    const handleSave = async (nom) => {
        const valStr = edits[nom];
        const v = parseFloat(valStr);
        if (!Number.isFinite(v) || v <= 0) {
            toast.error('Volume invalide (> 0)');
            return;
        }
        setSavingNom(nom);
        try {
            await axios.put(`${API}/api/articles/volume`, { nom, volume: v }, { withCredentials: true });
            toast.success(`Volume enregistré pour « ${nom} »`);
            setEdits(e => { const n = { ...e }; delete n[nom]; return n; });
            // Update locally
            setArticles(prev => prev.map(a => a.nom === nom ? { ...a, volume: v } : a));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setSavingNom(null); }
    };

    return (
        <div className="min-h-screen bg-slate-50 px-6 py-6">
            <div className="max-w-6xl mx-auto space-y-5">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-admin">
                        <ArrowLeft size={16} className="mr-1" /> Admin
                    </Button>
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Articles & Volumes
                        </h1>
                        <p className="text-xs text-slate-500">Gérer les volumes utilisés par le calculateur d'inventaire.</p>
                    </div>
                </div>

                {/* Stats */}
                <Card>
                    <CardContent className="pt-5 pb-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <CheckCircle2 size={20} className="text-emerald-600" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-emerald-700 tabular-nums">{counts.renseignes}</div>
                                    <div className="text-xs text-emerald-700/80">renseignés</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 border border-orange-100">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <AlertTriangle size={20} className="text-orange-600" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-orange-700 tabular-nums">{counts.manquants}</div>
                                    <div className="text-xs text-orange-700/80">à compléter</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-100 border border-slate-200">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <Save size={20} className="text-slate-500" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-slate-700 tabular-nums">{counts.total}</div>
                                    <div className="text-xs text-slate-600/80">articles total</div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Filtres */}
                <Card>
                    <CardContent className="pt-4 pb-4">
                        <div className="flex gap-3 items-center flex-wrap">
                            <div className="relative flex-1 min-w-[240px]">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    placeholder="Rechercher un article…"
                                    className="pl-9 h-9 text-sm"
                                    data-testid="articles-search"
                                />
                            </div>
                            <select
                                value={filterCat}
                                onChange={e => setFilterCat(e.target.value)}
                                className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                                data-testid="articles-cat-filter"
                            >
                                {categories.map(c => (
                                    <option key={c} value={c}>{c || 'Toutes catégories'}</option>
                                ))}
                            </select>
                        </div>
                    </CardContent>
                </Card>

                {/* Liste */}
                <Card>
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-base font-semibold text-slate-700">
                            {filtered.length} article{filtered.length > 1 ? 's' : ''}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {loading ? (
                            <p className="text-sm text-slate-400 italic text-center py-6">Chargement…</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table className="min-w-[640px]">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Article</TableHead>
                                            <TableHead>Catégorie</TableHead>
                                            <TableHead className="w-44">Volume actuel</TableHead>
                                            <TableHead className="w-56">Modifier</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filtered.map(a => {
                                            const isMissing = !a.volume || a.volume <= 0;
                                            const editVal = edits[a.nom];
                                            return (
                                                <TableRow key={a.nom} className={isMissing ? 'bg-orange-50/40' : ''}>
                                                    <TableCell className="font-medium text-slate-800">{a.nom}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200">{a.categorie || '—'}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {isMissing ? (
                                                            <span className="text-xs text-orange-700 font-medium">à renseigner</span>
                                                        ) : (
                                                            <span className="text-sm tabular-nums text-slate-700">{a.volume} m³</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={editVal ?? ''}
                                                                placeholder="0.00"
                                                                onChange={e => setEdits(prev => ({ ...prev, [a.nom]: e.target.value }))}
                                                                className="h-8 text-sm w-20"
                                                                data-testid={`vol-${a.nom}`}
                                                            />
                                                            <span className="text-xs text-slate-500">m³</span>
                                                            <Button
                                                                size="sm"
                                                                variant="default"
                                                                onClick={() => handleSave(a.nom)}
                                                                disabled={savingNom === a.nom || editVal === undefined || editVal === ''}
                                                                className="h-8"
                                                                data-testid={`save-${a.nom}`}
                                                            >
                                                                <Save size={12} />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {filtered.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center text-sm text-slate-400 italic py-6">
                                                    Aucun article
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default AdminArticlesPage;
